import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, Clock } from "lucide-react";
import { useApp } from "@/context/AppContext";

export default function GuidedTour() {
  const { tour, setTour, users, setUserId, setSelectedOpp } = useApp();
  const nav = useNavigate();
  const [elapsed, setElapsed] = useState(0);
  const step = tour.steps[tour.step];

  useEffect(() => {
    if (!tour.active || !step) return;
    const u = users.find((x) => x.role === step.role);
    if (u) setUserId(u.id);
    const m = step.route.match(/opp=([\w_]+)/);
    if (m) setSelectedOpp(m[1]);
    nav(step.route);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [tour.active, tour.step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!tour.active) return undefined;
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - tour.startedAt) / 1000)), 1000);
    return () => clearInterval(t);
  }, [tour.active, tour.startedAt]);

  if (!tour.active || !step) return null;
  const n = tour.steps.length;
  const go = (i) => setTour({ ...tour, step: Math.max(0, Math.min(n - 1, i)) });
  const exit = () => setTour({ active: false, step: 0, steps: [], startedAt: null });

  return (
    <div data-testid="guided-tour-panel" className="fixed bottom-4 left-1/2 z-[1300] w-[min(760px,94vw)] -translate-x-1/2 rounded-xl border border-[#F4B842]/40 bg-[#06142a]/95 p-4 shadow-2xl backdrop-blur-xl">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="rounded bg-[#F4B842] px-2 py-0.5 text-[11px] font-extrabold text-[#06142A]">GUIDED DEMO</span>
          <span data-testid="tour-step-indicator" className="text-[12px] text-slate-300">Step {tour.step + 1} of {n} · ~{step.minutes} min · viewing as <b className="text-white">{step.role}</b></span>
        </div>
        <div className="flex items-center gap-3 text-[12px] text-slate-400">
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")}</span>
          <button data-testid="tour-exit-btn" onClick={exit} className="flex items-center gap-1 rounded border border-slate-500/40 px-2 py-1 hover:text-white"><X className="h-3.5 w-3.5" />Exit</button>
        </div>
      </div>
      <div className="mt-3 flex gap-1">
        {tour.steps.map((s, i) => (
          <button key={s.step} data-testid={`tour-dot-${i + 1}`} onClick={() => go(i)} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= tour.step ? "bg-[#F4B842]" : "bg-slate-600"}`} title={s.title} />
        ))}
      </div>
      <h3 data-testid="tour-step-title" className="mt-3 text-lg font-bold text-white">{step.step}. {step.title}</h3>
      <p className="mt-1 text-[13.5px] leading-relaxed text-slate-300">{step.explain}</p>
      <p className="mt-1.5 text-[12.5px] italic text-[#5EEAD4]">Presenter: “{step.talk}”</p>
      <div className="mt-3 flex justify-between">
        <button data-testid="tour-prev-btn" disabled={tour.step === 0} onClick={() => go(tour.step - 1)} className="vig-btn-ghost flex items-center gap-1 px-3 py-1.5 text-[13px] disabled:opacity-40"><ChevronLeft className="h-4 w-4" />Previous</button>
        {tour.step < n - 1 ? (
          <button data-testid="tour-next-btn" onClick={() => go(tour.step + 1)} className="vig-btn-gold flex items-center gap-1 px-4 py-1.5 text-[13px]">Next step<ChevronRight className="h-4 w-4" /></button>
        ) : (
          <button data-testid="tour-finish-btn" onClick={() => { exit(); nav("/"); }} className="vig-btn-gold px-4 py-1.5 text-[13px]">Finish & return to Gateway</button>
        )}
      </div>
    </div>
  );
}
